import { useEffect, useRef } from 'react';
import CodeMirror from '@uiw/react-codemirror';
import { markdown, markdownLanguage } from '@codemirror/lang-markdown';
import { languages } from '@codemirror/language-data';
import { EditorView } from '@codemirror/view';
import { vscodeDark } from '@uiw/codemirror-theme-vscode';
import { cn } from '@/lib/utils';

interface MarkdownFieldProps {
  id?: string;
  value: string;
  onChange: (v: string) => void;
  rows?: number;
  placeholder?: string;
  invalid?: boolean;
  className?: string;
}

/**
 * CodeMirror 6 Markdown editor. No raw HTML — markdown only.
 */
export function MarkdownField({ id, value, onChange, rows = 16, placeholder, invalid, className }: MarkdownFieldProps) {
  const viewRef = useRef<EditorView | null>(null);

  // Ensure the editor re-applies theme when admin theme toggles.
  useEffect(() => {
    return () => {
      viewRef.current = null;
    };
  }, []);

  return (
    <div
      className={cn(
        'overflow-hidden rounded-md border bg-bg-elevated',
        invalid ? 'border-danger/60' : 'border-border',
        className,
      )}
    >
      <CodeMirror
        id={id}
        value={value}
        onChange={onChange}
        height={`${rows * 1.6}rem`}
        placeholder={placeholder}
        theme={vscodeDark}
        extensions={[markdown({ base: markdownLanguage, codeLanguages: languages }), EditorView.lineWrapping]}
        basicSetup={{
          lineNumbers: false,
          foldGutter: false,
          highlightActiveLine: false,
          highlightActiveLineGutter: false,
          autocompletion: false,
        }}
        onCreateEditor={(view) => {
          viewRef.current = view;
        }}
      />
    </div>
  );
}